import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { sceneAPI, playbookAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';

// Minimal Playbooks browser. A Playbook is a coach-owned binder of
// scenes — "Goal-line package", "3rd-and-long", "Friday's install" —
// and is the GoArmy-Edge equivalent of a printable playbook section.
// Backend collections + CRUD already exist (see scene_routes.py); this
// page is the front-end the coach needs to actually use them.
//
// Scope intentionally small for v1: list / create / rename / delete
// playbook, drag-drop scenes in/out via a simple checkbox modal,
// download the whole binder as one combined JSON file. Print-to-PDF
// and per-page wristband layouts are follow-ups.
export default function PlaybooksPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 8.5: redirect to coach dashboard if no domain in route or
  // query string. Adding a new domain doesn't require code edits — the
  // sidebar links the coach in with the right :domain segment.
  const domainId = domain || searchParams.get('domain');
  useEffect(() => {
    if (!domainId) {
      navigate('/coach', { replace: true });
    }
  }, [domainId, navigate]);

  const [playbooks, setPlaybooks] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPb, setEditingPb] = useState(null); // playbook obj when scene-picker open
  const [printingPb, setPrintingPb] = useState(null); // playbook obj when print preview open

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [pbResp, scResp] = await Promise.all([
        playbookAPI.list({ domain_id: domainId }),
        sceneAPI.list({ domain_id: domainId, limit: 500 }),
      ]);
      setPlaybooks(pbResp.data?.playbooks || []);
      setScenes(scResp.data?.scenes || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => { reload(); }, [reload]);

  const sceneById = useMemo(() => {
    const m = {};
    scenes.forEach((s) => { m[s.scene_id] = s; });
    return m;
  }, [scenes]);

  const handleCreate = async (name) => {
    if (!name?.trim()) return;
    try {
      await playbookAPI.create({
        domain_id: domainId,
        name: name.trim(),
        description: '',
        scene_ids: [],
      });
      setShowCreate(false);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Create failed');
    }
  };

  const handleRename = async (pb, name) => {
    if (!name?.trim() || name === pb.name) return;
    try {
      await playbookAPI.update(pb.playbook_id, { name: name.trim() });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Rename failed');
    }
  };

  const handleDelete = async (pb) => {
    const ok = await confirm({
      title: 'Delete playbook?',
      message: `Delete playbook "${pb.name}"? This does not delete the scenes inside.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await playbookAPI.remove(pb.playbook_id);
      toast.info('Playbook deleted');
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSetScenes = async (pb, sceneIds) => {
    try {
      await playbookAPI.update(pb.playbook_id, { scene_ids: sceneIds });
      setEditingPb(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleExportPlaybook = async (pb) => {
    // Bundle every scene in the playbook plus the playbook envelope into
    // a single JSON file. Coaches use this to share a complete binder
    // (scenes + ordering + cover) as one artifact.
    try {
      const sceneExports = await Promise.all(
        (pb.scene_ids || []).map(async (sid) => {
          try {
            const { data } = await sceneAPI.export(sid);
            return data;
          } catch (_) {
            return null;
          }
        })
      );
      const bundle = {
        format: 'scenario-os.playbook.v1',
        playbook: pb,
        scenes: sceneExports.filter(Boolean),
      };
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pb.name.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.playbook.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <CoachLayout title={`Playbooks — ${domainId}`} subtitle="Coach-owned binders of scenes">
      <div className="scene-library">
        <div className="scene-library-header">
          <h2>Playbooks</h2>
          <button className="btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            ＋ New playbook
          </button>
        </div>
        {error && <div className="scene-error">{error}</div>}
        {loading ? (
          <div>Loading playbooks…</div>
        ) : playbooks.length === 0 ? (
          <div className="empty-state" style={{ padding: 32, textAlign: 'center', opacity: 0.7 }}>
            No playbooks yet. Create one to bundle related scenes for install meetings, scout looks, or game-plan packages.
          </div>
        ) : (
          <div className="scene-library-grid">
            {playbooks.map((pb) => (
              <div key={pb.playbook_id} className="scene-card" style={{ borderTop: `4px solid ${pb.cover_color || '#1a3a6e'}` }}>
                <div className="scene-card-header">
                  <input
                    type="text"
                    defaultValue={pb.name}
                    onBlur={(e) => handleRename(pb, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    style={{
                      fontWeight: 600, fontSize: 16, border: 'none',
                      background: 'transparent', width: '100%', padding: 0,
                    }}
                  />
                </div>
                <div className="scene-card-desc">
                  {(pb.scene_ids || []).length} scene{(pb.scene_ids || []).length === 1 ? '' : 's'}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0', fontSize: 12, maxHeight: 100, overflowY: 'auto' }}>
                  {(pb.scene_ids || []).slice(0, 6).map((sid) => (
                    <li key={sid}
                      style={{ padding: '2px 0', cursor: 'pointer', textDecoration: 'underline', opacity: 0.85 }}
                      onClick={() => navigate(`/coach/scenes/${sid}?domain=${domainId}`)}
                      title="Open in Scene Designer"
                    >
                      {sceneById[sid]?.display_name || sid}
                    </li>
                  ))}
                  {(pb.scene_ids || []).length > 6 && (
                    <li style={{ opacity: 0.5 }}>+ {pb.scene_ids.length - 6} more…</li>
                  )}
                </ul>
                <div className="scene-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-secondary btn-sm" onClick={() => setEditingPb(pb)}>
                    ✎ Edit scenes
                  </button>
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => setPrintingPb(pb)}
                    title="Open the printable wristband view (use the browser's Save as PDF dialog)"
                  >
                    🖨 Print
                  </button>
                  <button className="btn-ghost btn-sm" onClick={() => handleExportPlaybook(pb)} title="Download playbook as a single JSON bundle">
                    ⬇ Export
                  </button>
                  <button className="btn-ghost btn-sm" onClick={() => handleDelete(pb)} title="Delete this playbook (scenes are kept)">
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreate && (
          <CreatePlaybookModal
            onClose={() => setShowCreate(false)}
            onCreate={handleCreate}
          />
        )}
        {editingPb && (
          <ScenePickerModal
            playbook={editingPb}
            scenes={scenes}
            onClose={() => setEditingPb(null)}
            onSave={(ids) => handleSetScenes(editingPb, ids)}
          />
        )}
        {printingPb && (
          <PrintablePlaybook
            playbook={printingPb}
            scenes={(printingPb.scene_ids || []).map((id) => sceneById[id]).filter(Boolean)}
            onClose={() => setPrintingPb(null)}
          />
        )}
      </div>
    </CoachLayout>
  );
}

// Sprint 4 — Printable wristband layout. Renders one card per scene
// with the field, formation/coverage, key actors, and the snap event's
// coaching script. Coaches use the browser's "Save as PDF" → 2-up
// landscape preset to get a paper wristband (Edge ships the same
// concept under "Print Playbook"). The CSS @media print rules below
// hide the Coach chrome and force one card per page break.
function PrintablePlaybook({ playbook, scenes, onClose }) {
  const printRef = useRef(null);
  const handlePrint = useCallback(() => {
    // Browser-native print is the right call here — generating real
    // PDFs server-side would require pulling in a heavyweight HTML→PDF
    // pipeline. The browser already produces excellent PDFs from CSS,
    // and coaches can choose paper size / margins themselves.
    window.print();
  }, []);

  return (
    <div className="scene-preview-modal playbook-print-modal" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="scene-preview-content playbook-print-shell"
        style={{ maxWidth: 980, padding: 0, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="playbook-print-toolbar" style={{
          padding: 12, borderBottom: '1px solid var(--coach-border)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <strong style={{ flex: 1 }}>🖨 Print preview — {playbook.name}</strong>
          <button className="btn-primary btn-sm" onClick={handlePrint}>
            Print / Save as PDF
          </button>
          <button className="btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div ref={printRef} className="playbook-print-body" style={{
          flex: 1, overflowY: 'auto', padding: 24, background: '#fff', color: '#111',
        }}>
          <PrintableHeader playbook={playbook} sceneCount={scenes.length} />
          {scenes.length === 0 && (
            <div style={{ opacity: 0.6, padding: 24 }}>This playbook has no scenes yet — add some from the Edit Scenes button.</div>
          )}
          {scenes.map((s, i) => (
            <PrintableSceneCard key={s.scene_id} scene={s} index={i + 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PrintableHeader({ playbook, sceneCount }) {
  return (
    <div style={{
      borderBottom: `4px solid ${playbook.cover_color || '#1a3a6e'}`,
      paddingBottom: 12, marginBottom: 16,
    }}>
      <h1 style={{ margin: 0, fontSize: 28, color: '#111' }}>{playbook.name}</h1>
      {playbook.description && (
        <p style={{ margin: '4px 0', color: '#444', fontSize: 13 }}>{playbook.description}</p>
      )}
      <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
        {sceneCount} scene{sceneCount === 1 ? '' : 's'} · Playbook ID {playbook.playbook_id}
      </div>
    </div>
  );
}

// Tiny SVG of the field + first-event actor positions. Renders crisply
// at any zoom and prints cleanly to PDF without rasterising.
function PrintableSceneCard({ scene, index }) {
  const firstEvent = (scene.timeline || [])[0];
  const states = firstEvent?.actor_states || {};

  return (
    <div className="playbook-print-card" style={{
      pageBreakInside: 'avoid',
      pageBreakAfter: 'always',
      borderTop: '1px solid #ccc',
      paddingTop: 12, marginTop: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          background: '#1a3a6e', color: '#fff', borderRadius: 4,
          padding: '2px 8px', fontWeight: 'bold', fontSize: 12,
        }}>{index}</span>
        <h2 style={{ margin: 0, fontSize: 18 }}>{scene.display_name}</h2>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666' }}>
          {scene.scene_type} · {scene.difficulty || 'beginner'}
        </span>
      </div>
      {scene.description && (
        <p style={{ fontSize: 12, color: '#444', margin: '4px 0' }}>{scene.description}</p>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
        <div style={{ flex: '0 0 380px' }}>
          <PrintableFieldSVG actors={scene.actors || []} states={states} />
        </div>
        <div style={{ flex: 1, fontSize: 12 }}>
          {scene.domain_metadata?.coverage_id && (
            <div style={{ marginBottom: 4 }}>
              <strong>Coverage:</strong> {scene.domain_metadata.coverage_id}
            </div>
          )}
          {scene.domain_metadata?.formation_id && (
            <div style={{ marginBottom: 4 }}>
              <strong>Formation:</strong> {scene.domain_metadata.formation_id}
            </div>
          )}
          {scene.domain_metadata?.read_progression && (
            <div style={{ marginBottom: 4 }}>
              <strong>Reads:</strong> {scene.domain_metadata.read_progression.join(' → ')}
            </div>
          )}
          {firstEvent?.coaching_script && (
            <div style={{ marginTop: 8, padding: 8, background: '#f4f4f4', borderRadius: 4 }}>
              {firstEvent.coaching_script}
            </div>
          )}
          {scene.tags?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 10, color: '#888' }}>
              {scene.tags.map((t) => (
                <span key={t} style={{ background: '#eee', padding: '1px 6px', borderRadius: 8, marginRight: 4 }}>
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PrintableFieldSVG({ actors, states }) {
  // Field is 100 yards (downfield x) × 53.3 yards (sideline y).
  // Render at 380×200px with margins for yardline labels.
  const W = 380, H = 200;
  const xs = (xField) => (xField / 100) * W;
  const ys = (yField) => H - (yField / 53.3) * H;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', border: '1px solid #ccc', background: '#1c4a1c' }}>
      {/* Yard lines */}
      {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((x) => (
        <line key={x} x1={xs(x)} y1={0} x2={xs(x)} y2={H} stroke="#fff" strokeOpacity="0.3" strokeWidth="0.5" />
      ))}
      {/* Hash marks */}
      {[0.333, 0.667].map((f) => (
        <line key={f} x1={0} y1={H * f} x2={W} y2={H * f} stroke="#fff" strokeOpacity="0.15" strokeWidth="0.4" />
      ))}
      {/* Actors */}
      {actors.map((a) => {
        const pos = states[a.actor_id]?.position || a.initial_state?.position;
        if (!pos) return null;
        const fill = a.team === 'offense' ? '#2c5d8c' : '#8b3a3a';
        return (
          <g key={a.actor_id}>
            <circle cx={xs(pos.x)} cy={ys(pos.y)} r={6} fill={fill} stroke="#fff" strokeWidth="1" />
            <text
              x={xs(pos.x)} y={ys(pos.y) + 2}
              fontSize="6" fill="#fff" textAnchor="middle"
              fontFamily="sans-serif" fontWeight="bold"
            >{a.label || a.role.slice(0, 2).toUpperCase()}</text>
          </g>
        );
      })}
    </svg>
  );
}

function CreatePlaybookModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  return (
    <div className="scene-preview-modal" onClick={onClose} role="dialog" aria-modal="true">
      <div className="scene-preview-content" style={{ maxWidth: 420, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New playbook</h3>
        <label>
          Name
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Goal-line package"
            style={{ width: '100%', marginTop: 4 }}
            onKeyDown={(e) => { if (e.key === 'Enter') onCreate(name); }}
          />
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" onClick={() => onCreate(name)} disabled={!name.trim()}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function ScenePickerModal({ playbook, scenes, onClose, onSave }) {
  const [selected, setSelected] = useState(() => new Set(playbook.scene_ids || []));
  const [filter, setFilter] = useState('');

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!filter.trim()) return scenes;
    const q = filter.trim().toLowerCase();
    return scenes.filter((s) =>
      (s.display_name || '').toLowerCase().includes(q)
      || (s.tags || []).some((t) => t.toLowerCase().includes(q))
      || (s.description || '').toLowerCase().includes(q)
    );
  }, [scenes, filter]);

  return (
    <div className="scene-preview-modal" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="scene-preview-content"
        style={{ maxWidth: 600, padding: 24, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Scenes in {playbook.name}</h3>
        <input
          type="text"
          placeholder="Filter scenes…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--coach-border)', borderRadius: 4, padding: 8 }}>
          {filtered.length === 0 && <div style={{ opacity: 0.5, padding: 12 }}>No scenes match.</div>}
          {filtered.map((s) => (
            <label key={s.scene_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <input
                type="checkbox"
                checked={selected.has(s.scene_id)}
                onChange={() => toggle(s.scene_id)}
              />
              <span style={{ flex: 1 }}>{s.display_name || s.scene_id}</span>
              <span style={{ fontSize: 11, opacity: 0.6 }}>{s.scene_type}</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'space-between' }}>
          <span style={{ alignSelf: 'center', opacity: 0.7, fontSize: 13 }}>
            {selected.size} scene{selected.size === 1 ? '' : 's'} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" onClick={() => onSave(Array.from(selected))}>
              ✓ Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
